import { forwardRef, type ReactNode } from 'react';
import {
	FormControl,
	FormLabel,
	FormHelperText,
	FormErrorMessage,
	Select as ChakraSelect,
	type SelectProps as ChakraSelectProps,
} from '@chakra-ui/react';

/**
 * Native Select primitive with the shared label/helper/error contract.
 *
 * For rich, searchable, or multi-select pickers use the `Dropdown` primitive
 * instead (popover/menu based).
 */
export interface SelectProps extends ChakraSelectProps {
	label?: ReactNode;
	helperText?: ReactNode;
	error?: ReactNode;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
	({ label, helperText, error, isRequired, isDisabled, children, ...selectProps }, ref) => (
		<FormControl isRequired={isRequired} isInvalid={Boolean(error)} isDisabled={isDisabled}>
			{label ? (
				<FormLabel
					fontSize="0.625rem"
					fontWeight={600}
					lineHeight={1}
					letterSpacing="0.07em"
					color="fg.muted"
					mb={2}
				>
					{label}
				</FormLabel>
			) : null}
			<ChakraSelect ref={ref} {...selectProps}>
				{children}
			</ChakraSelect>
			{error ? (
				<FormErrorMessage>{error}</FormErrorMessage>
			) : helperText ? (
				<FormHelperText color="fg.muted">{helperText}</FormHelperText>
			) : null}
		</FormControl>
	),
);
Select.displayName = 'Select';

export default Select;
